import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import * as client from './api.ts';
import { Button, Cell, Empty, Finding, Row, Section, Status, Table, type Tone } from './ui.tsx';

/**
 * The warden board, per AREA (migration 0143) — the live command view's list
 * of who is sweeping what.
 *
 * Product owner (2026-09-30): the admins and the Chief Warden see every area
 * with its warden, whether the warden was notified, whether they said they are
 * on it, and when it was done; a warden who does not reply or says they can't
 * is replaced here with one action. Every status is computed by the server from
 * the same timeline the signed report records, so this list cannot say
 * something the record does not.
 *
 * A word and a dot for every state — never colour alone (the greyscale test).
 */

export interface WardenArea {
  zoneId: string;
  zoneName: string;
  warden: { userId: string; name: string } | null;
  reassigned: boolean;
  assignedAt: string | null;
  assignedBy: string | null;
  notifiedAt: string | null;
  /**
   * How the area reached them in a way they can answer (0147): the app, or a
   * paid plan's text backup. Email is never a warden notice — wardens answer
   * in the app (owner ruling, 2026-10-01).
   */
  notifiedVia: 'push' | 'sms' | null;
  /** Has the app (a registered push device). Optional for older servers. */
  hasApp?: boolean;
  /** A backup text was sent or tried this turn. */
  texted?: boolean;
  notifyFailed: boolean;
  acknowledgedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  noResponseAt: string | null;
  sweep: { state: string; reportedAt: string; reportedBy: string | null } | null;
  status:
    | 'no_warden'
    | 'waiting'
    | 'queued'
    | 'not_reached'
    | 'no_app'
    | 'stalled'
    | 'overdue'
    | 'no_response'
    | 'declined'
    | 'on_it'
    | 'sweeping'
    | 'clear'
    | 'cannot_access'
    | 'needs_assistance';
  flagged: boolean;
  secondsWaiting: number | null;
  /** Seconds since they said "on it", while nothing is reported yet. */
  secondsOnIt: number | null;
  /** The warden's work mobile — only for those who may reassign. */
  wardenPhone?: string | null;
}

export interface WardenAreaBoard {
  scope: 'all' | 'mine';
  /** Can wardens without the app be texted? Paid plan and a reply number (0147). */
  smsBackup?: { available: boolean; reason: 'plan' | 'not_set_up' | null };
  canReassign: boolean;
  timeoutSeconds: number;
  eventState: string;
  procedure: string;
  areas: WardenArea[];
  history: {
    zoneId: string;
    zoneName: string;
    kind: string;
    warden: string;
    by: string | null;
    reason: string | null;
    at: string;
  }[];
}

export const getWardenAreas = (orgId: string, eventId: string) =>
  client.api<WardenAreaBoard>(`/orgs/${orgId}/events/${eventId}/warden-areas`);

/** Hand one area to somebody else; they are told at once, the previous warden released. */
export const reassignWardenArea = (
  orgId: string,
  eventId: string,
  zoneId: string,
  toUserId: string,
) =>
  client.api<{ assignedId: string }>(
    `/orgs/${orgId}/events/${eventId}/warden-areas/${zoneId}/reassign`,
    { method: 'POST', body: JSON.stringify({ toUserId }) },
  );

/** HH:MM in the viewer's clock — a time of day, which is what an incident log reads in. */
export function clock(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** The state of one area, in words, with its tone. */
export function reading(area: WardenArea): { word: string; tone: Tone } {
  const minutes = area.secondsWaiting !== null ? ` · ${Math.round(area.secondsWaiting / 60)}m` : '';
  switch (area.status) {
    case 'clear':
      return { word: 'Clear', tone: 'safe' };
    case 'cannot_access':
      return { word: 'Could not get in', tone: 'alert' };
    case 'needs_assistance':
      return { word: 'People still here', tone: 'alert' };
    case 'sweeping':
      return { word: 'Sweeping', tone: 'neutral' };
    case 'on_it':
      return {
        word: `On it${area.secondsOnIt !== null ? ` · ${Math.round(area.secondsOnIt / 60)}m` : ''}`,
        tone: 'neutral',
      };
    case 'stalled':
      return {
        word: `On it · ${Math.round((area.secondsOnIt ?? 0) / 60)}m, nothing reported`,
        tone: 'attention',
      };
    case 'not_reached':
      return { word: `Not reached${minutes}`, tone: 'attention' };
    case 'no_app':
      // Never "no answer": nobody could ask them about this area.
      return { word: "No app — can't be reached for this area", tone: 'attention' };
    case 'queued':
      // Busy with another of their areas — next, not silent.
      return { word: `Next for ${area.warden?.name ?? 'them'}`, tone: 'neutral' };
    case 'declined':
      return { word: "Can't do it", tone: 'attention' };
    case 'no_response':
    case 'overdue':
      return { word: `No answer${minutes}`, tone: 'attention' };
    case 'no_warden':
      return { word: 'Nobody assigned', tone: 'attention' };
    default:
      return { word: 'Waiting', tone: 'neutral' };
  }
}

function notified(area: WardenArea): string {
  if (!area.notifiedAt) return area.warden ? 'Not yet' : '—';
  const via = area.notifiedVia === 'sms' ? ' (text)' : '';
  return `${clock(area.notifiedAt)}${via}`;
}

/**
 * Whether a warden without the app can be reached at all (0147) — said, so
 * nobody waits on an answer that cannot come.
 */
export function smsLine(sms: WardenAreaBoard['smsBackup']): string {
  if (!sms) return '';
  if (sms.available) return 'Wardens without the app are texted and can reply by text.';
  if (sms.reason === 'not_set_up')
    return "Wardens answer in the EVAC app — the text backup isn't set up yet.";
  return 'Wardens answer in the EVAC app.';
}

/** One sentence about the whole board, from the same rows it renders. */
export function boardSentence(areas: WardenArea[]): string {
  if (areas.length === 0) return 'No areas are defined for this site.';
  const done = areas.filter((a) =>
    ['clear', 'cannot_access', 'needs_assistance'].includes(a.status),
  ).length;
  const flagged = areas.filter((a) => a.flagged).length;
  const base = `${done} of ${areas.length} ${areas.length === 1 ? 'area' : 'areas'} reported.`;
  if (flagged === 0) return base;
  return `${base} ${flagged} ${flagged === 1 ? 'needs' : 'need'} someone else.`;
}

/** Flagged first — the reason for the list — then by name, stable across refreshes. */
function ordered(areas: WardenArea[]): WardenArea[] {
  return [...areas].sort((a, b) =>
    a.flagged !== b.flagged ? (a.flagged ? -1 : 1) : a.zoneName.localeCompare(b.zoneName),
  );
}

const HISTORY_WORDS: Record<string, string> = {
  assigned: 'given',
  released: 'released from',
  acknowledged: 'on it for',
  declined: "can't do",
  no_response: 'no answer for',
  notified: 'told about',
};

export function WardenAreas({
  orgId,
  eventId,
  headcountOf,
  report,
  fallback = null,
}: {
  orgId: string;
  eventId: string;
  /** The sweep's headcount for an area, from the sweep board — one table, not two. */
  headcountOf?: (zoneId: string) => number | null;
  /** The control to file a sweep report from here, per area. */
  report?: (zone: { zoneId: string; zoneName: string }) => ReactNode;
  /** What to show somebody the server does not give the command board to. */
  fallback?: ReactNode;
}) {
  const board = useQuery({
    queryKey: ['wardenAreas', orgId, eventId],
    queryFn: () => getWardenAreas(orgId, eventId),
    refetchInterval: 5_000,
  });

  // Somebody the server refuses (a warden's own list lives in the app) sees
  // nothing here: this is the command surface.
  const status = (board.error as { status?: number } | null)?.status;
  if (board.error && (status === 403 || status === 404)) return <>{fallback}</>;
  if (board.error && !board.data) {
    return (
      <Section title="Wardens">
        <Finding>
          The warden list did not load.{' '}
          <Button
            variant="quiet"
            label="Retry the warden list"
            onClick={() => void board.refetch()}
          >
            Retry
          </Button>
        </Finding>
      </Section>
    );
  }
  if (!board.data) return <>{fallback}</>;
  if (board.data.scope !== 'all') {
    // A warden: their own areas first, each with the way to report it, then
    // whatever the fallback shows of everybody else's (the role lens puts
    // this pane first for them — docs/design-dashboard.md §9).
    const mine = ordered(board.data.areas);
    return (
      <div>
        {mine.length > 0 ? (
          <ul
            className="m-0 mb-3 list-none divide-y divide-[var(--line)] p-0"
            aria-label="Your areas"
          >
            {mine.map((a) => {
              const r = reading(a);
              return (
                <li key={a.zoneId} className="flex items-center gap-3 py-2 text-[14px]">
                  <span className="min-w-0 flex-1 truncate font-medium">{a.zoneName}</span>
                  <Status tone={r.tone}>{r.word}</Status>
                  {report?.({ zoneId: a.zoneId, zoneName: a.zoneName })}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mb-3 text-[13px] text-[var(--ink-2)]">No area is assigned to you.</p>
        )}
        {fallback}
      </div>
    );
  }
  const { areas, canReassign, history } = board.data;
  const holds = new Map<string, number>();
  for (const a of areas)
    if (a.warden) holds.set(a.warden.userId, (holds.get(a.warden.userId) ?? 0) + 1);

  return (
    <div>
      <p className="mb-3 text-[14px] text-[var(--ink-2)]">
        {boardSentence(areas)} {smsLine(board.data.smsBackup)}
        {board.isError ? (
          <span className="ml-2 text-[var(--ink-3)]">
            Not updating — showing {clock(new Date(board.dataUpdatedAt).toISOString())}.
          </span>
        ) : null}
      </p>
      {areas.length === 0 ? (
        <Empty>No areas are defined for this site, so nobody has an area to sweep.</Empty>
      ) : (
        <Table
          head={[
            'Area',
            'Warden',
            'Notified',
            'On it',
            'Done',
            'State',
            ...(headcountOf ? ['Count'] : []),
            '',
          ]}
        >
          {ordered(areas).map((a) => (
            <AreaRow
              key={a.zoneId}
              orgId={orgId}
              eventId={eventId}
              area={a}
              canReassign={canReassign}
              holds={holds}
              headcount={headcountOf ? headcountOf(a.zoneId) : undefined}
              report={report?.({ zoneId: a.zoneId, zoneName: a.zoneName })}
            />
          ))}
        </Table>
      )}
      {history.length > 0 ? (
        <ul className="mt-3 space-y-1 text-[13px] text-[var(--ink-2)]" aria-label="Warden changes">
          {history.slice(0, 8).map((h, i) => (
            <li key={`${h.at}-${i}`}>
              {clock(h.at)} · {h.warden} {HISTORY_WORDS[h.kind] ?? h.kind} {h.zoneName}
              {h.by && h.kind === 'assigned' ? ` (by ${h.by})` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function AreaRow({
  orgId,
  eventId,
  area,
  canReassign,
  holds,
  headcount,
  report,
}: {
  orgId: string;
  eventId: string;
  area: WardenArea;
  canReassign: boolean;
  holds: Map<string, number>;
  /** undefined = no sweep column; null = no count yet. */
  headcount?: number | null;
  report?: ReactNode;
}) {
  const state = reading(area);
  const done = ['clear', 'cannot_access', 'needs_assistance'].includes(area.status);
  return (
    <Row active={area.flagged}>
      <Cell wide>{area.zoneName}</Cell>
      <Cell>
        {area.warden ? (
          <span>
            {area.warden.name}
            {area.reassigned ? (
              <span className="ml-1 text-[12px] text-[var(--ink-3)]">reassigned</span>
            ) : null}
            {area.wardenPhone ? (
              // The manual fallback when delivery fails: call them.
              <a
                className="ml-2 text-[12px] underline"
                href={`tel:${area.wardenPhone}`}
                aria-label={`Call ${area.warden.name}`}
              >
                Call
              </a>
            ) : null}
          </span>
        ) : (
          '—'
        )}
      </Cell>
      <Cell>
        {area.notifyFailed && !area.notifiedAt ? (
          <Status tone="attention">Not reached</Status>
        ) : (
          notified(area)
        )}
      </Cell>
      <Cell>{clock(area.acknowledgedAt)}</Cell>
      <Cell>{done && area.sweep ? clock(area.sweep.reportedAt) : '—'}</Cell>
      <Cell>
        <Status tone={state.tone}>{state.word}</Status>
        {area.status === 'declined' && area.declineReason ? (
          <span className="ml-2 text-[12px] text-[var(--ink-3)]">{area.declineReason}</span>
        ) : null}
      </Cell>
      {headcount !== undefined ? <Cell numeric>{headcount ?? '—'}</Cell> : null}
      <Cell>
        <span className="flex flex-wrap items-center gap-2">
          {canReassign && area.status !== 'clear' ? (
            <Reassign orgId={orgId} eventId={eventId} area={area} holds={holds} />
          ) : null}
          {report}
        </span>
      </Cell>
    </Row>
  );
}

/**
 * Pick anybody at staff level or above — including somebody who already holds
 * other areas (their load is shown). They are told immediately; the previous
 * warden is released. "Tell them again" re-sends to the current holder.
 */
function Reassign({
  orgId,
  eventId,
  area,
  holds,
}: {
  orgId: string;
  eventId: string;
  area: WardenArea;
  holds: Map<string, number>;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const roster = useQuery({
    queryKey: ['roster', orgId],
    queryFn: () => client.getRoster(orgId),
    enabled: open,
  });
  const go = useMutation({
    mutationFn: (target: string) => reassignWardenArea(orgId, eventId, area.zoneId, target),
    onSuccess: () => {
      setOpen(false);
      setTo('');
      void qc.invalidateQueries({ queryKey: ['wardenAreas', orgId, eventId] });
    },
  });
  const error = client.errorMessage(go.error);
  const silent = ['waiting', 'overdue', 'no_response', 'not_reached', 'stalled'].includes(
    area.status,
  );

  if (!open) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        {silent && area.warden ? (
          <Button
            variant="quiet"
            disabled={go.isPending}
            label={`Tell ${area.warden.name} again about ${area.zoneName}`}
            onClick={() => go.mutate(area.warden!.userId)}
          >
            Tell again
          </Button>
        ) : null}
        <Button
          variant={area.flagged ? 'secondary' : 'quiet'}
          label={`Reassign ${area.zoneName}`}
          onClick={() => setOpen(true)}
        >
          Reassign
        </Button>
        {error ? <Finding>{error}</Finding> : null}
      </span>
    );
  }

  const candidates = (roster.data?.members ?? [])
    .filter(
      (m) =>
        m.deprovisioned_at === null &&
        ['owner', 'admin', 'staff'].includes(m.role) &&
        m.user_id !== area.warden?.userId,
    )
    .map((m) => ({
      id: m.user_id,
      name: m.display_name && m.display_name.length > 0 ? m.display_name : (m.email ?? 'Unnamed'),
      load: holds.get(m.user_id) ?? 0,
    }))
    // Free people first, then by name.
    .sort((a, b) => a.load - b.load || a.name.localeCompare(b.name));

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <select
        className="h-8 rounded-[var(--radius-1)] border border-[var(--line-strong)] bg-[var(--paper)] px-2 text-[13px] text-[var(--ink)]"
        value={to}
        onChange={(ev) => setTo(ev.target.value)}
        aria-label={`Who sweeps ${area.zoneName} instead`}
      >
        <option value="">{roster.isError ? 'People did not load' : 'Choose a person…'}</option>
        {candidates.map((m) => (
          <option key={m.id} value={m.id}>
            {m.load > 0 ? `${m.name} (holds ${m.load})` : m.name}
          </option>
        ))}
      </select>
      <Button
        variant="primary"
        disabled={!to || go.isPending}
        label={`Give ${area.zoneName} to the chosen person`}
        onClick={() => go.mutate(to)}
      >
        Give it to them
      </Button>
      <Button
        variant="quiet"
        label={`Cancel reassigning ${area.zoneName}`}
        onClick={() => setOpen(false)}
      >
        Cancel
      </Button>
      {error ? <Finding>{error}</Finding> : null}
    </span>
  );
}
